"use client";

import { useEffect, useRef } from "react";
import { CLASS_META, type Classification } from "@/lib/chess/winchance";

/**
 * Two-column move list. `current` is the number of plies applied (0 = start).
 * Doubles as the table view for the evaluation graph.
 */
export function MoveList({
  moves,
  current,
  onSelect,
  classes,
  blackFirst = false,
  startMove = 1,
}: {
  moves: { san: string }[];
  current: number;
  onSelect: (ply: number) => void;
  classes?: (Classification | undefined)[];
  blackFirst?: boolean;
  startMove?: number;
}) {
  const box = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const container = box.current;
    const active = container?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!container || !active) return;
    const bounds = container.getBoundingClientRect(), row = active.getBoundingClientRect();
    if (row.bottom > bounds.bottom) container.scrollTop += row.bottom - bounds.bottom;
    else if (row.top < bounds.top) container.scrollTop -= bounds.top - row.top;
  }, [current]);

  const rows: { n: number; w?: number; b?: number }[] = [];
  const offset = blackFirst ? 1 : 0;
  for (let i = 0; i < moves.length; i++) {
    const slot = i + offset;
    const r = Math.floor(slot / 2);
    rows[r] ??= { n: startMove + r };
    if (slot % 2 === 0) rows[r].w = i;
    else rows[r].b = i;
  }

  const cell = (i?: number) => {
    if (i === undefined) return <span className="px-2 py-1 text-muted">…</span>;
    const cls = classes?.[i];
    const meta = cls ? CLASS_META[cls] : null;
    const active = current === i + 1;
    return (
      <button
        onClick={() => onSelect(i + 1)}
        aria-current={active}
        title={meta?.label}
        className={`flex items-center justify-between rounded px-2 py-1 text-left font-mono text-sm ${active ? "bg-accent-soft font-semibold" : "hover:bg-surface-2"}`}
      >
        <span>{moves[i].san}</span>
        {meta?.symbol && (
          <span className={`ml-1 text-xs font-bold ${meta.tone}`} aria-label={meta.label}>
            {meta.symbol}
          </span>
        )}
      </button>
    );
  };

  return (
    <ol ref={box} className="max-h-72 overflow-y-auto rounded-lg border border-border bg-surface p-1" aria-label="Moves">
      {rows.length === 0 && <li className="px-2 py-1 text-sm text-muted">No moves yet.</li>}
      {rows.map((r) => (
        <li key={r.n} className="grid grid-cols-[2.5rem_1fr_1fr] items-center gap-1">
          <span className="text-right text-xs text-muted">{r.n}.</span>
          {cell(r.w)}
          {r.b !== undefined || r.w !== undefined ? (r.b !== undefined ? cell(r.b) : <span />) : null}
        </li>
      ))}
    </ol>
  );
}
